import React from 'react';
import {
  VStack, HStack, Text, Icon, Pressable, Box,
} from '@gluestack-ui/themed';
import {
  Check, Play, Lock, Video, ChevronRight,
} from 'lucide-react-native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withRepeat,
  withSequence,
  withTiming,
  Easing,
} from 'react-native-reanimated';
import { Task } from '@/student/types';
import { isEventDeliveredTask } from '@/common/utils/tasks/isEventDeliveredTask';

const AnimatedBox = Animated.createAnimatedComponent(Box);

export type TaskStatus = 'completed' | 'current' | 'upcoming' | 'locked';

interface TimelineTaskProps {
  task: Task;
  status: TaskStatus;
  isSelected: boolean;
  allTasks: Task[];
  levelOrder: number[];
  onPress: () => void;
  testID?: string;
}

/**
 * Get unlock requirements message for locked events
 */
const getUnlockMessage = (task: Task, allTasks: Task[], levelOrder: number[]): string => {
  const currentTaskLevelIndex = levelOrder.indexOf(task.level!);

  const requiredTasks = allTasks.filter((t) => {
    if (t.id === task.id) return false;
    const taskLevelIndex = levelOrder.indexOf(t.level!);
    if (taskLevelIndex < currentTaskLevelIndex) return true;
    if (taskLevelIndex === currentTaskLevelIndex && (t.sortIndex || 0) < (task.sortIndex || 0)) return true;
    return false;
  });

  const incompleteTasks = requiredTasks.filter((t) => t.submission?.state !== 'marked_as_done');

  if (incompleteTasks.length === 0) {
    return 'All tasks complete - unlocking soon';
  }
  if (incompleteTasks.length === 1) {
    return `Complete "${incompleteTasks[0].title}" first`;
  }
  return `Complete ${incompleteTasks.length} more tasks to unlock`;
};

/**
 * TimelineTask
 * ------------
 * An individual task item in the journey timeline.
 */
export const TimelineTask: React.FC<TimelineTaskProps> = ({
  task,
  status,
  isSelected,
  allTasks,
  levelOrder,
  onPress,
  testID,
}) => {
  const isEvent = isEventDeliveredTask(task);
  const isLocked = status === 'locked';

  const pulse = useSharedValue(1);

  React.useEffect(() => {
    if (status === 'current') {
      pulse.value = withRepeat(
        withSequence(
          withTiming(1.15, { duration: 1200, easing: Easing.inOut(Easing.ease) }),
          withTiming(1, { duration: 1200, easing: Easing.inOut(Easing.ease) }),
        ),
        -1,
        false,
      );
    }
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const dotAnimatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: status === 'current' ? pulse.value : 1 }],
  }));

  return (
    <Pressable
      onPress={onPress}
      pl="$0"
      pr="$4"
      py="$3"
      bg={isSelected ? '$bgCard' : 'transparent'}
      borderRadius="$xl"
      borderWidth={1}
      borderColor={isSelected ? '$borderLight' : 'transparent'}
      sx={{
        ':hover': {
          borderColor: isSelected ? '$borderLight' : '$borderLight',
          bg: isSelected ? '$bgCard' : '$bgSubtle',
        },
      }}
      testID={testID}
    >
      {({ pressed }) => (
        <HStack space="md" alignItems="center">
          {/* Left Column: Semantic Dot Indicator */}
          <Box w={32} alignItems="center" justifyContent="center">
            <AnimatedBox
              width={22}
              height={22}
              borderRadius={11}
              justifyContent="center"
              alignItems="center"
              borderWidth={2}
              zIndex={1}
              backgroundColor="$bgApp"
              style={dotAnimatedStyle}
              borderColor={status === 'current' ? '$brandBase' : '$neutral300'}
            >
              {status === 'completed' ? (
                <Box bg="$statusSuccess" w="$full" h="$full" borderRadius="$full" justifyContent="center" alignItems="center">
                  <Icon
                    as={Check}
                    size="xs"
                    color="white"
                    // @ts-expect-error - strokeWidth is supported by lucide icons
                    strokeWidth={3}
                  />
                </Box>
              ) : status === 'current' ? (
                <Box bg="$brandBase" w="$full" h="$full" borderRadius="$full" justifyContent="center" alignItems="center">
                  <Icon as={Play} size="xs" color="white" fill="white" />
                </Box>
              ) : status === 'locked' ? (
                <Box bg="$neutral100" w="$full" h="$full" borderRadius="$full" justifyContent="center" alignItems="center">
                  <Icon as={Lock} size="xs" color="$textDisabled" />
                </Box>
              ) : (
                <Box
                  bg="transparent"
                  w={6}
                  h={6}
                  borderRadius="$full"
                  borderWidth={2}
                  borderColor="$neutral300"
                />
              )}
            </AnimatedBox>
          </Box>

          {/* Right Column: Content Card */}
          <HStack flex={1} alignItems="center" space="sm">
            <VStack flex={1} space="xs">
              <HStack space="sm" alignItems="center">
                <Text
                  size="sm"
                  fontWeight={status === 'current' ? 'bold' : 'medium'}
                  color={isLocked ? '$textDisabled' : status === 'current' ? '$textHeading' : '$textBody'}
                  numberOfLines={1}
                  sx={{
                    ':hover': { opacity: 0.8 },
                  }}
                  opacity={pressed ? 0.8 : 1}
                >
                  {task.title}
                </Text>
                {isEvent && (
                  <HStack
                    bg={isLocked ? '$statusWarningSubtle' : '$bgSubtle'}
                    px="$1.5"
                    py="$0.5"
                    borderRadius="$md"
                    borderWidth={1}
                    borderColor={isLocked ? '$statusWarningSubtle' : '$borderLight'}
                    space="sm"
                    alignItems="center"
                  >
                    <Icon as={isLocked ? Lock : Video} size="sm" color={isLocked ? '$statusWarning' : '$textHint'} />
                    <Text
                      size="2xs"
                      fontWeight="bold"
                      color={isLocked ? '$statusWarning' : '$textSubtle'}
                      textTransform="uppercase"
                    >
                      {isLocked ? 'Locked' : 'Event'}
                    </Text>
                  </HStack>
                )}
              </HStack>

              {isLocked && isEvent && (
                <Text size="2xs" color="$statusWarning" fontStyle="italic">
                  {getUnlockMessage(task, allTasks, levelOrder)}
                </Text>
              )}
            </VStack>

            <Icon as={ChevronRight} size="sm" color={status === 'upcoming' ? '$textHint' : '$textSubtle'} />
          </HStack>
        </HStack>
      )}
    </Pressable>
  );
};

export default TimelineTask;
